import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * FloatingEdgeTabs
 *
 * Fixed vertical tab on the right edge linking to the Knowledge Base.
 * It is centred vertically but automatically lifts clear of the floating
 * corner buttons (call / WhatsApp / selfie / voice note) on short screens,
 * and hides itself when a viewport is too short to hold both.
 */

const CORNER_SELECTOR = '[data-floating-corner="right"]';
const CORNER_GAP = 16; // breathing room between the tab and the corner buttons
const HEADER_GAP = 76; // keep the tab clear of the sticky header
const COMPACT_HEIGHT = 820; // below this the tab wears its short label so it still fits

/** Distance from the bottom of the viewport, in px — centred where there is room. */
const useSafeBottom = (ref, pathname, compact, setCompact) => {
  const [bottom, setBottom] = useState(null);
  const [tooShort, setTooShort] = useState(false);

  useLayoutEffect(() => {
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      setCompact(window.innerHeight < COMPACT_HEIGHT);
      const tabHeight = el.offsetHeight;
      const viewport = window.innerHeight;

      let clearance = 24;
      document.querySelectorAll(CORNER_SELECTOR).forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.height > 0) clearance = Math.max(clearance, viewport - rect.top + CORNER_GAP);
      });

      const highest = viewport - tabHeight - HEADER_GAP;
      if (highest < clearance) {
        setTooShort(true);
        return;
      }
      const centred = (viewport - tabHeight) / 2;
      setTooShort(false);
      setBottom(Math.round(Math.min(Math.max(centred, clearance), highest)));
    };

    compute();
    // corner buttons live inside lazily-rendered pages, so measure again once they land
    const retry = setTimeout(compute, 500);
    window.addEventListener('resize', compute, { passive: true });
    window.addEventListener('orientationchange', compute);
    return () => {
      clearTimeout(retry);
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, [ref, pathname, compact, setCompact]);

  return { bottom, tooShort };
};

const FloatingEdgeTab = ({ side = 'right', label, compactLabel, labelEmphasis, to, hideOnPaths = [] }) => {
  const location = useLocation();
  const ref = useRef(null);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [compact, setCompact] = useState(false);
  const isRight = side === 'right';

  const currentPath = location.pathname;
  const hidden =
    currentPath.startsWith('/admin') ||
    currentPath.startsWith('/cv/') ||
    currentPath === to ||
    hideOnPaths.some((path) => currentPath === path || currentPath.startsWith(path));

  const { bottom, tooShort } = useSafeBottom(ref, currentPath, compact, setCompact);

  const sessionKey = `floatingTab_${side}_pulsed`;
  const clickedKey = `floatingTab_${side}_clicked`;

  // One nudge per session, once the reader is half way down the page
  useLayoutEffect(() => {
    if (hidden) return undefined;
    try {
      if (sessionStorage.getItem(sessionKey) || sessionStorage.getItem(clickedKey)) return undefined;
    } catch {
      return undefined;
    }

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if ((window.scrollY / scrollable) * 100 < 50) return;
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), 2000);
      try {
        sessionStorage.setItem(sessionKey, 'true');
      } catch {
        /* storage blocked — the nudge simply repeats next load */
      }
      window.removeEventListener('scroll', onScroll);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hidden, sessionKey, clickedKey]);

  const handleClick = () => {
    try {
      sessionStorage.setItem(clickedKey, 'true');
    } catch {
      /* ignore */
    }
  };

  if (hidden) return null;

  const renderLabel = () => {
    const text = compact && compactLabel ? compactLabel : label;
    if (!labelEmphasis) return text;
    const parts = text.split(labelEmphasis);
    if (parts.length !== 2) return text;
    return (
      <>
        {parts[0]}
        <span style={{ fontWeight: 700, textDecoration: 'underline' }}>{labelEmphasis}</span>
        {parts[1]}
      </>
    );
  };

  const resting = isRight ? 'none' : 'rotate(180deg)';
  const lifted = isRight ? 'translateX(-4px)' : 'rotate(180deg) translateX(-4px)';

  return (
    <Link
      ref={ref}
      to={to}
      onClick={handleClick}
      data-testid={`floating-tab-${side}`}
      className={`floating-edge-tab floating-edge-tab-${side} ${shouldPulse ? 'pulse-animation' : ''}`}
      style={{
        position: 'fixed',
        bottom: bottom === null ? '50%' : `${bottom}px`,
        [isRight ? 'right' : 'left']: 0,
        zIndex: 40,
        visibility: tooShort ? 'hidden' : 'visible',
        pointerEvents: tooShort ? 'none' : 'auto',

        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        transform: resting,

        background: 'linear-gradient(135deg, #01A9B4 0%, #006A71 100%)',
        color: 'white',
        padding: '1.5rem 0.75rem',
        fontSize: '0.78rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        textDecoration: 'none',

        border: '2px solid white',
        borderRight: isRight ? 'none' : '2px solid white',
        borderLeft: isRight ? '2px solid white' : 'none',
        borderRadius: isRight ? '14px 0 0 14px' : '0 14px 14px 0',
        boxShadow: isRight
          ? '-4px 0 12px rgba(1, 169, 180, 0.3)'
          : '4px 0 12px rgba(1, 169, 180, 0.3)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, bottom 0.25s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = lifted;
        e.currentTarget.style.boxShadow = isRight
          ? '-6px 0 20px rgba(1, 169, 180, 0.5)'
          : '6px 0 20px rgba(1, 169, 180, 0.5)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = resting;
        e.currentTarget.style.boxShadow = isRight
          ? '-4px 0 12px rgba(1, 169, 180, 0.3)'
          : '4px 0 12px rgba(1, 169, 180, 0.3)';
      }}
    >
      {renderLabel()}

      <style>{`
        @media (max-width: 768px) {
          .floating-edge-tab {
            padding: 1rem 0.5rem !important;
            font-size: 0.7rem !important;
          }
        }
        @media (max-height: 700px) {
          .floating-edge-tab {
            padding: 0.9rem 0.5rem !important;
            font-size: 0.68rem !important;
          }
        }

        @keyframes pulse-wiggle {
          0%, 100% {
            box-shadow: ${isRight ? '-4px 0 12px rgba(1, 169, 180, 0.3)' : '4px 0 12px rgba(1, 169, 180, 0.3)'};
            transform: ${resting};
          }
          25% {
            box-shadow: ${isRight ? '-6px 0 20px rgba(212, 175, 55, 0.6)' : '6px 0 20px rgba(212, 175, 55, 0.6)'};
            transform: ${isRight ? 'translateY(-3px)' : 'rotate(180deg) translateY(-3px)'};
            background: linear-gradient(135deg, #D4AF37 0%, #01A9B4 100%);
          }
          50% {
            box-shadow: ${isRight ? '-8px 0 24px rgba(1, 169, 180, 0.6)' : '8px 0 24px rgba(1, 169, 180, 0.6)'};
            transform: ${resting};
            background: linear-gradient(135deg, #01A9B4 0%, #006A71 100%);
          }
          75% {
            box-shadow: ${isRight ? '-6px 0 20px rgba(212, 175, 55, 0.6)' : '6px 0 20px rgba(212, 175, 55, 0.6)'};
            transform: ${isRight ? 'translateY(3px)' : 'rotate(180deg) translateY(3px)'};
            background: linear-gradient(135deg, #D4AF37 0%, #01A9B4 100%);
          }
        }

        @keyframes pulse-color-only {
          0%, 100% {
            box-shadow: ${isRight ? '-4px 0 12px rgba(1, 169, 180, 0.3)' : '4px 0 12px rgba(1, 169, 180, 0.3)'};
            background: linear-gradient(135deg, #01A9B4 0%, #006A71 100%);
          }
          50% {
            box-shadow: ${isRight ? '-8px 0 24px rgba(212, 175, 55, 0.6)' : '8px 0 24px rgba(212, 175, 55, 0.6)'};
            background: linear-gradient(135deg, #D4AF37 0%, #01A9B4 100%);
          }
        }

        .pulse-animation { animation: pulse-wiggle 2s ease-in-out; }

        @media (prefers-reduced-motion: reduce) {
          .pulse-animation { animation: pulse-color-only 2s ease-in-out; }
        }
      `}</style>
    </Link>
  );
};

const FloatingEdgeTabs = () => {
  const location = useLocation();
  const isOnArticlePage =
    location.pathname.startsWith('/knowledge-base/') && location.pathname !== '/knowledge-base';

  return (
    <FloatingEdgeTab
      side="right"
      label={isOnArticlePage ? 'More Articles — Read HERE' : 'Knowledge Base — Learn HERE'}
      compactLabel={isOnArticlePage ? 'More Articles' : 'Knowledge Base'}
      labelEmphasis="HERE"
      to="/knowledge-base"
      hideOnPaths={['/knowledge-base']}
    />
  );
};

export default FloatingEdgeTabs;
